import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, PrimaryButton } from './ui/UI';
/**
 * Validation Snapshot — the wedge offering.
 *
 * Previously: a 3-step pretend-product UI ("pick your environment, pick your
 * concern, get a fake read-out") that synthesized fake findings and fake
 * time-to-value estimates. That's the canonical fake-product-demo AI tell:
 * looks interactive, isn't real, generates copy that pretends to be insight.
 *
 * Now: a plain editorial section that explains what a real Snapshot
 * delivers. No fake form, no fake findings, no synthetic "5-7 days" promise.
 * If a buyer wants the actual Snapshot, they click through to early access —
 * which is what they were going to do anyway.
 */
const delivers = [{
  title: 'A short list of validated attack paths.',
  body: 'Not 500 findings. The paths that actually lead to business impact in your environment — with the evidence behind each one.'
}, {
  title: 'A per-control verdict.',
  body: 'Which of your EDR, SIEM, MFA, WAF, identity, and posture tools detected, blocked, logged, alerted — and which missed entirely.'
}, {
  title: 'A remediation order.',
  body: 'The fixes that close the most paths first, mapped to the ticketing systems and owners your team already uses.'
}, {
  title: 'A verification plan.',
  body: 'How Periscan will re-test once the fix lands. Closed tickets are not the finish line — closed paths are.'
}, {
  title: 'A shareable evidence pack.',
  body: 'A summary your team, customers, auditors, insurers, or board can read without a decoder ring. And a technical appendix for the people who want one.'
}];
export function ValidationSnapshot() {
  return <section id="validation-snapshot" aria-labelledby="snapshot-heading" className="relative w-full py-32 border-t border-white/5 scroll-mt-24" data-id="element-1383">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1384">
        <SectionLabel number="—" label="Validation Snapshot · The first proof moment" className="mb-8" data-id="element-1385" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-1386">
          <div className="lg:col-span-7" data-id="element-1387">
            <motion.h2 id="snapshot-heading" initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[1.05] text-white" data-id="element-1388">
              Start with proof.
              <br data-id="element-1389" />
              <span className="text-white/40" data-id="element-1390">Not a procurement cycle.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-1391">
            <p className="text-lg text-white/65 leading-relaxed" data-id="element-1392">
              A Validation Snapshot is the first proof moment — focused, scoped,
              and delivered without a six-week sales motion. Five things you
              get. Each one specific. None of them simulated.
            </p>
          </div>
        </div>

        <ol className="border-t border-white/5 mb-16" data-id="element-1393">
          {delivers.map((d, i) => <motion.li key={d.title} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="border-b border-white/5 py-10 grid grid-cols-12 gap-6 items-baseline" data-id="element-1394">
              <span className="col-span-12 md:col-span-1 font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em]" data-id="element-1395">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="col-span-12 md:col-span-4 text-2xl md:text-3xl text-white font-semibold tracking-tight leading-snug" data-id="element-1396">
                {d.title}
              </h3>
              <p className="col-span-12 md:col-span-7 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl" data-id="element-1397">
                {d.body}
              </p>
            </motion.li>)}
        </ol>

        <PrimaryButton href="/early-access" size="lg" data-id="element-1398">
          Claim a Validation Snapshot
        </PrimaryButton>
      </div>
    </section>;
}